import { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import { ChevronDown, Check } from 'lucide-react';



export default function KebonyMaintenance() {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="min-h-screen bg-background text-foreground">
      <Navbar />

      <div className="pt-20 pb-20">
        {/* Hero Section */}
        <div className="relative h-[500px] md:h-[600px] mb-32 overflow-hidden rounded-[12px]">
          <img
            src="https://brookstimber.com/wp-content/uploads/2025/01/kebony25Body-1024x768.jpg"
            alt="Kebony kledning"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/30 to-transparent flex flex-col justify-end p-12">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
            >
              <p className="text-sm tracking-widest-2xl font-body uppercase text-white mb-6">Vedlikehold & Kvalitet</p>
              <h1 className="font-heading text-4xl md:text-5xl lg:text-6xl font-light leading-[1.1] text-white mb-6">
                Kebony Kledning
                <br />
                <span className="italic">som holder</span>
              </h1>
              <p className="font-body text-white/80 text-lg max-w-2xl leading-relaxed">
                Miljøvennlig, langvarig og krever minimalt vedlikehold. La din badstue eldes naturlig til vakker sølvgrå patina.
              </p>
            </motion.div>
          </div>
        </div>

        <div className="max-w-[1440px] mx-auto px-6 md:px-12">

          {/* About Kebony */}
          <motion.section
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.1 }}
            className="mb-32"
          >
            <div className="grid lg:grid-cols-2 gap-16 items-center">
              <div>
                <p className="text-sm tracking-widest-2xl font-body uppercase text-primary mb-6">Materialer</p>
                <h2 className="font-heading text-4xl md:text-5xl font-light leading-[1.1] mb-8 text-[#2a1810]">
                  <span className="italic">Hva</span> er Kebony?
                </h2>
            
            <p className="font-body text-foreground/70 text-lg leading-relaxed mb-8">
              Kebony er en miljøvennlig, patentert teknologi utviklet i Norge som forsterker egenskapene til bærekraftig bartre. Teknologien bruker bio-basert furfurylalkohol for permanent modifisering av trecellevegger.
            </p>

            <p className="font-body text-foreground/70 text-lg leading-relaxed">
              Resultatet er tre med karakteristikker av de beste hardtreslottene, kombinert med en rik brun farge som naturlig eldes til vakker sølvgrå patina.
            </p>
            </div>
            <div className="rounded-[8px] overflow-hidden h-[400px]">
            <img
              src="https://brookstimber.com/wp-content/uploads/2021/10/kebonyClear-1.jpg"
              alt="Kebony Close-up"
              className="w-full h-full object-cover"
            />
            </div>
            </div>

            <div className="grid md:grid-cols-3 gap-8 mt-16">
              <motion.div whileHover={{ y: -4 }} className="bg-secondary/50 rounded-[8px] p-8">
                <div className="flex items-center gap-3 mb-4">
                  <Check size={24} className="text-primary" />
                  <h3 className="font-heading text-lg font-light text-[#2a1810]">Hardhet</h3>
                </div>
                <p className="font-body text-foreground/70">Hardhet av de beste hardtreslottene</p>
              </motion.div>

              <motion.div whileHover={{ y: -4 }} className="bg-secondary/50 rounded-[8px] p-8">
                <div className="flex items-center gap-3 mb-4">
                  <Check size={24} className="text-primary" />
                  <h3 className="font-heading text-lg font-light text-[#2a1810]">Stabilitet</h3>
                </div>
                <p className="font-body text-foreground/70">Redusert svelling og krymping</p>
              </motion.div>

              <motion.div whileHover={{ y: -4 }} className="bg-secondary/50 rounded-[8px] p-8">
                <div className="flex items-center gap-3 mb-4">
                  <Check size={24} className="text-primary" />
                  <h3 className="font-heading text-lg font-light text-[#2a1810]">Varighet</h3>
                </div>
                <p className="font-body text-foreground/70">30 års utendørs levetidsgaranti</p>
              </motion.div>
            </div>
          </motion.section>

          {/* Types */}
          <motion.section
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.3 }}
            className="mb-32"
          >
            <h2 className="font-heading text-4xl font-light mb-16">Kebony Clear vs Character</h2>
            
            <div className="grid md:grid-cols-2 gap-12">
              <div>
                <div className="rounded-lg overflow-hidden h-64 mb-8">
                  <img
                    src="https://brookstimber.com/wp-content/uploads/2021/10/kebonyClear-1.jpg"
                    alt="Kebony Clear"
                    className="w-full h-full object-cover"
                  />
                </div>
                <h3 className="font-heading text-2xl font-light mb-6">Kebony Clear</h3>
                <p className="font-serif text-foreground/70 mb-8">
                  Glatt, ren overflate fra Pinus Radiata. Moderne utseende, ideell for videre bearbeiding.
                </p>
              </div>

              <div>
                <div className="rounded-lg overflow-hidden h-64 mb-8">
                  <img
                    src="https://brookstimber.com/wp-content/uploads/2021/10/kebonyCharacter-1.jpg"
                    alt="Kebony Character"
                    className="w-full h-full object-cover"
                  />
                </div>
                <h3 className="font-heading text-2xl font-light mb-6">Kebony Character</h3>
                <p className="font-serif text-foreground/70 mb-8">
                  Med knuter og rustikk karakter fra Pinus Sylvestris. Tradisjonelt utseende, autentisk sjarm.
                </p>
              </div>
            </div>
          </motion.section>

          {/* Maintenance */}
          <motion.section
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.3 }}
            className="mb-32"
          >
            <p className="text-sm tracking-widest-2xl font-body uppercase text-primary mb-6">Vedlikehold</p>
            <h2 className="font-heading text-4xl md:text-5xl font-light leading-[1.1] mb-16 text-[#2a1810]">
              Enkel og <span className="italic">effektiv</span> pleie
            </h2>

            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
              {[
                { num: '01', title: 'Inspeksjon', desc: 'Gjennomfør visuell inspeksjon av alle kledningsflater årlig.' },
                { num: '02', title: 'Rengjøring', desc: 'Bruk mildt såpevann og myk børste. Unngå høytrykksspyling.' },
                { num: '03', title: 'Naturlig Patina', desc: 'La Kebony eldes naturlig til vakker sølvgrå farge.' },
                { num: '04', title: 'Ventilasjon', desc: 'Sikre god luftsirkulasjon rundt badstuen året rundt.' },
                { num: '05', title: 'Snø & Regn', desc: 'Fjern tunge snølaster og sikre god drenering.' },
                { num: '06', title: 'Sikkerhet', desc: 'Inspiser for mugg eller fuktskader ved behov.' }
              ].map((item, idx) => (
                <motion.div
                  key={idx}
                  whileHover={{ y: -8 }}
                  className="bg-white rounded-[8px] p-8 border border-border/20 hover:border-primary/30 transition-colors"
                >
                  <span className="font-heading text-4xl font-light text-primary/40 mb-4 block">{item.num}</span>
                  <h3 className="font-heading text-xl font-light mb-3 text-[#2a1810]">{item.title}</h3>
                  <p className="font-body text-foreground/70">{item.desc}</p>
                </motion.div>
              ))}
            </div>
          </motion.section>

          {/* Types */}
          <motion.section
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="mb-32"
          >
            <p className="text-sm tracking-widest-2xl font-body uppercase text-primary mb-6">Varianter</p>
            <h2 className="font-heading text-4xl md:text-5xl font-light leading-[1.1] mb-16 text-[#2a1810]">
              Clear eller
              <br />
              <span className="italic">Character</span>
            </h2>

            <div className="grid md:grid-cols-2 gap-12">
              <motion.div whileHover={{ y: -8 }}>
                <div className="rounded-[8px] overflow-hidden h-80 mb-8">
                  <img
                    src="https://brookstimber.com/wp-content/uploads/2021/10/kebonyClear-1.jpg"
                    alt="Kebony Clear"
                    className="w-full h-full object-cover hover:scale-110 transition-transform duration-500"
                  />
                </div>
                <h3 className="font-heading text-2xl font-light mb-4 text-[#2a1810]">Kebony Clear</h3>
                <p className="font-body text-foreground/70 mb-4">
                  Glatt og ren overflate fra Pinus Radiata. Moderne, perfekt for videre bearbeiding.
                </p>
                <ul className="space-y-2 text-base font-body text-foreground/70">
                  <li className="flex items-center gap-2"><Check size={16} className="text-primary" /> Moderne utseende</li>
                  <li className="flex items-center gap-2"><Check size={16} className="text-primary" /> Maskinbar</li>
                  <li className="flex items-center gap-2"><Check size={16} className="text-primary" /> Ren overflate</li>
                </ul>
              </motion.div>

              <motion.div whileHover={{ y: -8 }}>
                <div className="rounded-[8px] overflow-hidden h-80 mb-8">
                  <img
                    src="https://brookstimber.com/wp-content/uploads/2021/10/kebonyCharacter-1.jpg"
                    alt="Kebony Character"
                    className="w-full h-full object-cover hover:scale-110 transition-transform duration-500"
                  />
                </div>
                <h3 className="font-heading text-2xl font-light mb-4 text-[#2a1810]">Kebony Character</h3>
                <p className="font-body text-foreground/70 mb-4">
                  Med knuter og rustikk karakter fra Pinus Sylvestris. Tradisjonelt og autentisk.
                </p>
                <ul className="space-y-2 text-base font-body text-foreground/70">
                  <li className="flex items-center gap-2"><Check size={16} className="text-primary" /> Rustikk sjarm</li>
                  <li className="flex items-center gap-2"><Check size={16} className="text-primary" /> Tradisjonelt utseende</li>
                  <li className="flex items-center gap-2"><Check size={16} className="text-primary" /> Autentisk</li>
                </ul>
              </motion.div>
            </div>
          </motion.section>

          {/* Gallery */}
          <motion.section
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.4 }}
            className="mb-32"
          >
            <p className="text-sm tracking-widest-2xl font-body uppercase text-primary mb-6">Galeri</p>
            <h2 className="font-heading text-4xl md:text-5xl font-light leading-[1.1] mb-16 text-[#2a1810]">
              Kebony i
              <br />
              <span className="italic">praksis</span>
            </h2>

            <div className="grid md:grid-cols-2 gap-8">
              <motion.div whileHover={{ y: -8 }} className="rounded-[8px] overflow-hidden h-96">
                <img
                  src="https://brookstimber.com/wp-content/uploads/2025/01/kebony25Thumb4.jpg"
                  alt="Kebony i praksis"
                  className="w-full h-full object-cover hover:scale-110 transition-transform duration-500"
                />
              </motion.div>
              <motion.div whileHover={{ y: -8 }} className="rounded-[8px] overflow-hidden h-96">
                <img
                  src="https://brookstimber.com/wp-content/uploads/2025/01/kebony25Thumb5.jpg"
                  alt="Kebony fasade"
                  className="w-full h-full object-cover hover:scale-110 transition-transform duration-500"
                />
              </motion.div>
            </div>
          </motion.section>

          {/* FAQ */}
          <motion.section
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.5 }}
            className="mb-20"
          >
            <p className="text-sm tracking-widest-2xl font-body uppercase text-primary mb-6">FAQ</p>
            <h2 className="font-heading text-4xl md:text-5xl font-light leading-[1.1] mb-16 text-[#2a1810]">Ofte stilte
              <br />
              <span className="italic">spørsmål</span>
            </h2>
            <FAQAccordion />
          </motion.section>

          {/* CTA */}
          <motion.section
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.6 }}
            className="bg-gradient-to-br from-[#2a1810] to-[#3d2818] text-white rounded-[8px] p-12 md:p-20 text-center"
          >
            <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.7 }}>
              <p className="text-sm tracking-widest-2xl font-body uppercase text-white/60 mb-6">Neste steg</p>
              <h2 className="font-heading text-3xl md:text-4xl font-light leading-[1.2] mb-6">
                Velg Kebony
                <br />
                <span className="italic">for din badstue</span>
              </h2>
              <p className="font-body text-lg text-white/70 mb-12 max-w-2xl mx-auto">
                Med Kebony-kledning får du en badstue som er vakker, holdbar og krever minimalt vedlikehold. Eldes naturlig og beskyttet i mange tiår.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <motion.a
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  href="/sauna-umi-konfigurator"
                  className="px-8 py-4 bg-white text-[#2a1810] font-body tracking-widest text-sm font-semibold rounded-[4px] hover:bg-white/90 transition-all inline-block"
                >
                  START KONFIGURATOR
                </motion.a>
                <motion.a
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  href="/be-om-tilbud"
                  className="px-8 py-4 border-2 border-white text-white font-body tracking-widest text-sm font-semibold rounded-[4px] hover:bg-white/10 transition-all inline-block"
                >
                  BE OM TILBUD
                </motion.a>
              </div>
            </motion.div>
          </motion.section>
        </div>
      </div>

      <Footer />
    </div>
  );
}

// Update FAQAccordion styling
function FAQAccordion() {
  const [expanded, setExpanded] = useState(null);

  const faqs = [
    {
      q: 'Trenger Kebony maling eller olje?',
      a: 'Nei, Kebony trenger ikke maling eller olje. Materialet er behandlet og vil eldes naturlig til en vakker sølvgrå patina. Hvis du ønsker å bevare den opprinnelige fargen, kan du bruke UV-beskyttende lak, men det er ikke nødvendig.',
    },
    {
      q: 'Hvor lenge varer Kebony?',
      a: 'Kebony har 30 års utendørs levetidsgaranti. Med riktig vedlikehold og pleie kan det vare langt lenger, gjerne 50+ år eller mer.',
    },
    {
      q: 'Hva betyr den sølvgrå patinas?',
      a: 'Den sølvgrå patinas er helt naturlig og er tegn på at treet eldes på riktig måte. Det er ikke tegn på forringelse - tvert imot beskytter det treet ytterligere mot UV-stråler og værutsettelse.',
    },
    {
      q: 'Kan jeg rengjøre Kebony?',
      a: 'Ja, du kan rengjøre Kebony med mildt såpevann og en myk børste. Unngå høytrykksspyling da det kan skade overflaten og fjerne den beskyttende patinas.',
    },
    {
      q: 'Er Kebony sikkert rundt oppvarming?',
      a: 'Ja, Kebony er sikker og giftfritt. Det inneholder ingen giftige impregnering og er således trygt å bruke både innvendig og utvendig rundt en badstue.',
    },
    {
      q: 'Hva er forskjellen mellom Kebony Clear og Character?',
      a: 'Kebony Clear har en glatt, ren overflate og moderne utseende (fra Pinus Radiata), mens Kebony Character har knuter og rustikk karakter (fra Pinus Sylvestris). Clear kan maskineres videre, mens Character bør ikke maskineres for å unngå å eksponere ubehandlet kjernetre.',
    },
  ];

  return (
    <div className="max-w-3xl mx-auto space-y-4">
      {faqs.map((item, idx) => (
        <motion.div key={idx} whileHover={{ y: -2 }} className="border border-border/20 rounded-[6px] overflow-hidden hover:border-primary/30 transition-colors">
          <button
            onClick={() => setExpanded(expanded === idx ? null : idx)}
            className="w-full flex items-center justify-between p-6 bg-white hover:bg-secondary/50 transition-colors text-left"
          >
            <h3 className="font-heading text-lg font-light pr-4 text-[#2a1810]">{item.q}</h3>
            <ChevronDown
              size={20}
              className={`flex-shrink-0 text-primary transition-transform duration-300 ${expanded === idx ? 'rotate-180' : ''}`}
            />
          </button>
          {expanded === idx && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              exit={{ opacity: 0, height: 0 }}
              transition={{ duration: 0.2 }}
              className="bg-secondary/30 border-t border-border/20 px-6 py-4"
            >
              <p className="font-body text-foreground/70 leading-relaxed">{item.a}</p>
            </motion.div>
          )}
        </motion.div>
      ))}
    </div>
  );
}